import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { calendarApi } from "@/services/api";
import type { EventType } from "@/types";
import { Calendar, Clock, MapPin, FileText, Tag, Loader2, AlertCircle } from "lucide-react";

const eventTypes: { value: EventType; label: string }[] = [
  { value: "company_event", label: "Firmenevent" },
  { value: "meeting", label: "Besprechung" },
  { value: "holiday", label: "Feiertag" },
  { value: "birthday", label: "Geburtstag" },
  { value: "arzttermin", label: "Arzttermin" },
  { value: "urlaub", label: "Urlaub" },
  { value: "other", label: "Sonstiges" }
];

export function EventFormPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    event_type: "company_event" as EventType,
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    is_all_day: false,
    timezone: "Europe/Berlin",
    is_public: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Build datetime strings
      let startDateTime: string;
      let endDateTime: string | undefined;
      
      if (formData.is_all_day) {
        startDateTime = `${formData.start_date} 00:00:00`;
        if (formData.end_date) {
          endDateTime = `${formData.end_date} 23:59:59`;
        }
      } else {
        const startTime = formData.start_time ? `${formData.start_time}:00` : "00:00:00";
        startDateTime = `${formData.start_date} ${startTime}`;
        if (formData.end_date && formData.end_time) {
          endDateTime = `${formData.end_date} ${formData.end_time}:00`;
        }
      }
      
      const payload = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        event_type: formData.event_type,
        start_date: startDateTime,
        end_date: endDateTime,
        is_all_day: formData.is_all_day,
        timezone: formData.timezone,
        is_public: formData.is_public
      };
      
      const response = await calendarApi.create(payload);
      
      if (response.data.success) {
        navigate("/calendar", { 
          state: { 
            message: "Terminvorschlag wurde eingereicht und muss genehmigt werden." 
          }
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Fehler beim Speichern des Termins");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Neuer Termin</h1>
        <p className="text-gray-500 mt-1">
          Erstellen Sie einen neuen Terminvorschlag für den Firmenkalender.
        </p>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titel *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="z.B. Team Meeting"
          />
        </div>
        
        {/* Event Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Tag className="h-4 w-4 inline mr-1" />
            Termintyp
          </label>
          <select
            value={formData.event_type}
            onChange={(e) => handleChange("event_type", e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {eventTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* All Day Toggle */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_all_day"
            checked={formData.is_all_day}
            onChange={(e) => handleChange("is_all_day", e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="is_all_day" className="ml-2 text-sm text-gray-700">
            Ganztägiger Termin
          </label>
        </div>
        
        {/* Date/Time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              Startdatum *
            </label>
            <input
              type="date"
              required
              value={formData.start_date}
              onChange={(e) => handleChange("start_date", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          
          {!formData.is_all_day && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="h-4 w-4 inline mr-1" />
                Startzeit *
              </label>
              <input
                type="time"
                required={!formData.is_all_day}
                value={formData.start_time}
                onChange={(e) => handleChange("start_time", e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enddatum (optional)
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => handleChange("end_date", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          
          {!formData.is_all_day && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Endzeit (optional)
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => handleChange("end_time", e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          )}
        </div>
        
        {/* Timezone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Clock className="h-4 w-4 inline mr-1" />
            Zeitzone
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => handleChange("timezone", e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="Europe/Berlin">Berlin (CET/CEST)</option>
            <option value="Europe/London">London (GMT/BST)</option>
            <option value="Europe/Paris">Paris (CET/CEST)</option>
            <option value="Europe/Vienna">Wien (CET/CEST)</option>
            <option value="UTC">UTC</option>
          </select>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MapPin className="h-4 w-4 inline mr-1" />
            Ort
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange("location", e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="z.B. Konferenzraum A"
          />
        </div>
        
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <FileText className="h-4 w-4 inline mr-1" />
            Beschreibung
          </label>
          <textarea
            rows={4}
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="Details zum Termin..."
          />
        </div>
        
        {/* Submit */}
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-gray-500">
            * Pflichtfelder
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate("/calendar")}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Termin vorschlagen
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
